import { Divider, MenuList, SvgIcon } from '@mui/material';
import React from 'react';

import { DATA_TEST } from '../../enums';
import { CustomUtilityOptions, IUtilityTrayOptions, UtilityNames, UtilityOptions } from '../../types';

import { MobileProfileOptionsMenu } from './MobileProfileOptionsMenu';
import { MobileUtilityMenuOption } from './MobileUtilityMenuOption';

interface IMobileUtilityMainMenuProps extends IUtilityTrayOptions {
  firstTrayMenuItemRef?: React.MutableRefObject<HTMLLIElement>;
  setActiveSubmenu(option: UtilityNames): void;
  closeUtilityMenu(): void;
}

interface IUtilityComponentOptions {
  name: UtilityNames;
  utilityOptions: UtilityOptions | CustomUtilityOptions;
  label?: string;
  url?: string;
  customMuiIcon?: typeof SvgIcon;
  stringIcon?: string;
}

export const MobileUtilityMainMenu: React.FC<IMobileUtilityMainMenuProps> = ({
  helpOptions,
  reportsOptions,
  notificationsOptions,
  settingsOptions,
  profileSettingsOptions,
  customUtilityOptions,
  firstTrayMenuItemRef,
  setActiveSubmenu,
  closeUtilityMenu
}) => {
  const showProfileSettingsOptions =
    profileSettingsOptions?.updateProfileUrl ||
    profileSettingsOptions?.handleSignOut ||
    profileSettingsOptions?.updateProfileOnClick;

  // Consolidated to reduce cyclomatic complexity
  // Utility order is determined by its position in the array
  const utilityComponentOptions: IUtilityComponentOptions[] = [
    {
      name: UtilityNames.custom,
      label: customUtilityOptions?.utilityName,
      utilityOptions: customUtilityOptions as CustomUtilityOptions,
      customMuiIcon: customUtilityOptions?.muiIcon,
      stringIcon: customUtilityOptions?.stringIcon
    },
    {
      name: UtilityNames.notifications,
      label: UtilityNames.notifications,
      utilityOptions: notificationsOptions as UtilityOptions
    },
    {
      name: UtilityNames.reports,
      label: UtilityNames.reports,
      utilityOptions: reportsOptions as UtilityOptions
    },
    { name: UtilityNames.help, label: UtilityNames.help, utilityOptions: helpOptions as UtilityOptions },
    {
      name: UtilityNames.settings,
      label: UtilityNames.settings,
      utilityOptions: settingsOptions as UtilityOptions
    }
  ];

  const emptyUtilityTray = utilityComponentOptions.every((option) => !option.utilityOptions);

  const showDivider = showProfileSettingsOptions && !emptyUtilityTray;

  return (
    <MenuList data-test={DATA_TEST.mobile_utility_main_menu_container}>
      {utilityComponentOptions
        // Remove options that don't have utilityOptions
        .filter((option) => option.utilityOptions)
        .map((option, idx) => {
          const { utilityOptions } = option;
          return (
            <MobileUtilityMenuOption
              // After removing options without utilityOptions,
              // we can safely assume the first option should receive autoFocus
              autoFocus={idx === 0}
              handleClick={(e) => {
                if (utilityOptions.iconOnClickOverride) {
                  closeUtilityMenu();
                  utilityOptions.iconOnClickOverride(e);
                  return;
                }
                if (utilityOptions.iconUrl) {
                  closeUtilityMenu();
                  return;
                }
                setActiveSubmenu(option.name);
              }}
              customMuiIcon={option.customMuiIcon}
              stringIcon={option.stringIcon}
              key={option.name}
              label={option.label ?? ''}
              badgeContent={utilityOptions.badgeContent}
              badgeVariant={utilityOptions.badgeVariant}
              hideChevron={Boolean(utilityOptions.iconOnClickOverride || utilityOptions.iconUrl)}
              url={utilityOptions.iconUrl}
              linkComponent={utilityOptions.iconLinkComponent}
              firstTrayMenuItemRef={idx === 0 ? firstTrayMenuItemRef : undefined}
            />
          );
        })}
      {showDivider && <Divider sx={{ mx: 2 }} />}
      {showProfileSettingsOptions && (
        <MobileProfileOptionsMenu
          firstTrayMenuItemRef={emptyUtilityTray ? firstTrayMenuItemRef : undefined}
          autoFocusFirstItem={emptyUtilityTray}
          updateProfileLinkComponent={profileSettingsOptions.updateProfileLinkComponent}
          updateProfileUrl={profileSettingsOptions.updateProfileUrl}
          updateProfileOnClick={profileSettingsOptions.updateProfileOnClick}
          handleSignOut={profileSettingsOptions.handleSignOut}
          closeUtilityMenu={closeUtilityMenu}
        />
      )}
    </MenuList>
  );
};
